// =====================================================================
// pdfBranding.js — design COMMUN de tous les PDF de course (04.11.2026) :
// listes de départ, résultats de manche, résultats de l'épreuve, analyse,
// rapport de l'arbitre…
//
// Saisie centralisée dans Course & exports → « En-tête et pied de page des
// PDF » (RaceSheetForm.jsx), champs de la config de la course :
//   logoLeft, logoRight   logos en haut à gauche / à droite (data URL)
//   bannerImage           bandeau en bas de page (data URL)
//   bannerHeightCm        hauteur du bandeau, 2 à 4 cm (défaut 3)
//   footerText            texte libre (facultatif)
//   timing, dataprocessing
//
// Pied de page de chaque page :
//   [bandeau sponsors pleine largeur]
//   ─────────────────────────────────────────────────────────────
//   Created 04.11.2026 08:15   Timing: X  [logo 321-timing]  Data processing: Y   Page 1/2
//
// Le logo 321-timing (/logo/logo.png) est chargé une fois par
// ensureOwnLogo() — à appeler (await) avant de générer un PDF.
// À placer dans src/utils/.
// =====================================================================

const MARGIN = 14;
let OWN_LOGO = null;
let ownLogoTried = false;

async function urlToDataUrl(url) {
    const res = await fetch(url, { cache: "force-cache" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
    });
}

export async function ensureOwnLogo(url = "/logo/logo.png") {
    if (OWN_LOGO || ownLogoTried) return OWN_LOGO;
    ownLogoTried = true;
    try {
        OWN_LOGO = await urlToDataUrl(url);
    } catch {
        OWN_LOGO = null; // pas de logo : le pied de page reste en texte
    }
    return OWN_LOGO;
}

export function setOwnLogo(dataUrl) {
    OWN_LOGO = dataUrl || null;
    ownLogoTried = true;
}

export function ownLogo() {
    return OWN_LOGO;
}

function imageSize(doc, dataUrl, maxW, maxH) {
    const p = doc.getImageProperties(dataUrl);
    let h = maxH;
    let w = (p.width / p.height) * h;
    if (w > maxW) {
        w = maxW;
        h = (p.height / p.width) * w;
    }
    return { w, h };
}

function addImage(doc, dataUrl, x, y, maxW, maxH, align = "left") {
    if (!dataUrl) return 0;
    try {
        const { w, h } = imageSize(doc, dataUrl, maxW, maxH);
        const fmt = /^data:image\/(png|jpe?g|webp)/i.exec(dataUrl)?.[1]?.toUpperCase().replace("JPG", "JPEG") || undefined;
        const px = align === "right" ? x - w : align === "center" ? x - w / 2 : x;
        doc.addImage(dataUrl, fmt, px, y, w, h);
        return w;
    } catch {
        return 0; // image illisible : ignorée plutôt que de bloquer le PDF
    }
}

// Logos du haut (gauche / droite).
export function brandHeader(doc, config) {
    const W = doc.internal.pageSize.getWidth();
    addImage(doc, config.logoLeft, MARGIN, 7, 34, 16, "left");
    addImage(doc, config.logoRight, W - MARGIN, 7, 34, 16, "right");
}

function bannerMm(config) {
    if (!config.bannerImage) return 0;
    const cm = Math.min(4, Math.max(2, Number(config.bannerHeightCm) || 3));
    return cm * 10;
}

// Marge du bas à réserver (tableaux autoTable : margin.bottom).
export function brandBottomMargin(config) {
    return 18 + bannerMm(config);
}

function stamp() {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Pied de page sur TOUTES les pages (à appeler une fois le document fini).
export function brandFooter(doc, config) {
    const n = doc.getNumberOfPages();
    const created = `Created ${stamp()}`;
    const banner = bannerMm(config);
    for (let i = 1; i <= n; i++) {
        doc.setPage(i);
        const H = doc.internal.pageSize.getHeight();
        const W = doc.internal.pageSize.getWidth();
        // Bandeau (sponsors) tout en bas, pleine largeur utile.
        if (banner) addImage(doc, config.bannerImage, W / 2, H - 5 - banner, W - 2 * MARGIN, banner, "center");
        const lineY = H - 7 - banner - 9;
        doc.setDrawColor(0);
        doc.setLineWidth(0.3);
        doc.line(MARGIN, lineY, W - MARGIN, lineY);
        const ty = lineY + 5.5;
        doc.setFont(undefined, "normal");
        doc.setFontSize(7);
        doc.setTextColor(0);
        doc.text([config.footerText, created].filter(Boolean).join("   "), MARGIN, ty);
        doc.text(`Page ${i}/${n}`, W - MARGIN, ty, { align: "right" });
        // Centre : « Timing: X  [logo]  Data processing: Y »
        const left = config.timing ? `Timing: ${config.timing}` : "";
        const right = config.dataprocessing ? `Data processing: ${config.dataprocessing}` : "";
        const logo = OWN_LOGO;
        let lw = 0;
        let lh = 0;
        if (logo) {
            try {
                ({ w: lw, h: lh } = imageSize(doc, logo, 26, 7));
            } catch {
                lw = 0;
            }
        }
        const gap = 3;
        const wl = left ? doc.getTextWidth(left) : 0;
        const wr = right ? doc.getTextWidth(right) : 0;
        const total = wl + (lw ? lw + 2 * gap : gap) + wr;
        let x = W / 2 - total / 2;
        if (left) doc.text(left, x, ty);
        x += wl + (lw ? gap : gap / 2);
        if (lw) {
            addImage(doc, logo, x, ty - lh + 1.6, 26, 7, "left");
            x += lw + gap;
        }
        if (right) doc.text(right, x, ty);
    }
}

// Préchargement dès l'import (navigateur) : le logo est prêt au premier PDF.
if (typeof window !== "undefined" && typeof fetch !== "undefined") ensureOwnLogo();